import { createApi } from "@reduxjs/toolkit/query/react";
import toast from "react-hot-toast";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import {
  API_COMPANY_FILES,
  API_COMPANY_FILE_META,
  API_COMPANY_FILE_RENAME,
} from "@/constants/apiEndpoints";
import type {
  IFileSingleResponse,
  IListFilesParams,
  IListFilesResponse,
  IRenameFilePayload,
} from "@/types/(company)/files.interface";

export const companyFilesApi = createApi({
  reducerPath: "companyFilesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["CompanyFiles", "CompanyFile"],
  endpoints: (builder) => ({
    listCompanyFiles: builder.query<IListFilesResponse, IListFilesParams>({
      query: (params) => ({
        url: API_COMPANY_FILES,
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((file) => ({
                type: "CompanyFile" as const,
                id: file.fileId,
              })),
              { type: "CompanyFiles" as const, id: "LIST" },
            ]
          : [{ type: "CompanyFiles" as const, id: "LIST" }],
    }),

    getCompanyFile: builder.query<IFileSingleResponse, { fileId: string }>({
      query: ({ fileId }) => ({
        url: API_COMPANY_FILE_META(fileId),
        method: "GET",
      }),
      providesTags: (_r, _e, { fileId }) => [
        { type: "CompanyFile" as const, id: fileId },
      ],
    }),

    renameCompanyFile: builder.mutation<IFileSingleResponse, IRenameFilePayload>({
      query: ({ fileId, originalName }) => ({
        url: API_COMPANY_FILE_RENAME(fileId),
        method: "PATCH",
        body: { originalName },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        const toastId = toast.loading("Renaming file...");
        try {
          await queryFulfilled;
          toast.success("File renamed", { id: toastId });
        } catch (err) {
          console.error("Rename failed", err);
          toast.error("Failed to rename file", { id: toastId });
        }
      },
      invalidatesTags: (_r, _e, { fileId }) => [
        { type: "CompanyFiles", id: "LIST" },
        { type: "CompanyFile", id: fileId },
      ],
    }),
  }),
});

export const {
  useListCompanyFilesQuery,
  useGetCompanyFileQuery,
  useRenameCompanyFileMutation,
} = companyFilesApi;
